import { forwardRef, useId } from "react";
import type { ButtonHTMLAttributes, ReactElement, ReactNode } from "react";
import { cloneElement, isValidElement } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { humanize } from "../format";

export function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

/* ---------- buttons ---------- */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "danger" | "danger-solid" | "ghost";
  size?: "sm";
  /** Shown in the page head on desktop only; the phone action bar carries it instead. */
  barDup?: boolean;
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, barDup, busy, className, children, disabled, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        "btn",
        variant === "primary" && "primary",
        variant === "danger" && "danger",
        variant === "danger-solid" && "danger solid",
        variant === "ghost" && "ghost",
        size === "sm" && "sm",
        barDup && "bar-dup",
        className,
      )}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {children}
    </button>
  );
});

/* ---------- badges ---------- */

export type BadgeKind =
  "ok" | "warn" | "high" | "bad" | "info" | "acc" | "mute" | "purple" | "neutral" | "faint";

export function Badge({ kind = "mute", children }: { kind?: BadgeKind; children: ReactNode }) {
  return <span className={`badge b-${kind}`}>{children}</span>;
}

/** Colour for every status the API returns, keyed by its humanized label (the prototype's STATUS_KIND). */
export const STATUS_KIND: Record<string, BadgeKind> = {
  New: "info",
  Contacted: "info",
  Engaged: "acc",
  Qualified: "ok",
  Nurture: "mute",
  Disqualified: "bad",
  Discovery: "info",
  Scoping: "info",
  Proposal: "acc",
  Negotiation: "warn",
  "Closed Won": "ok",
  "Closed Lost": "bad",
  Draft: "mute",
  Sent: "info",
  Viewed: "acc",
  Accepted: "ok",
  Declined: "bad",
  Expired: "mute",
  Superseded: "mute",
  Planned: "info",
  Active: "ok",
  "On hold": "warn",
  Paused: "warn",
  Completed: "ok",
  Closed: "mute",
  Cancelled: "bad",
  Canceled: "bad",
  "To do": "mute",
  Todo: "mute",
  "In progress": "info",
  Blocked: "bad",
  Done: "ok",
  Pending: "mute",
  Submitted: "acc",
  Approved: "ok",
  "Changes requested": "warn",
  Rejected: "bad",
  Open: "info",
  Paid: "ok",
  Void: "mute",
  Invoiced: "info",
  Scheduled: "mute",
  Enabled: "ok",
  Invited: "warn",
  Disabled: "mute",
  "Active - AI Bot": "info", // the user automated processing is recorded as
  Locked: "bad",
  Processed: "ok",
  Revoked: "mute",
  Waived: "mute",
  Maintenance: "info",
  Live: "ok",
  Uncollectible: "bad",
  Overdue: "bad",
  Warranty: "acc", // a closed project inside its warranty (UX-194)
};

/** A status from the API ("closed_won") as a coloured badge ("Closed Won"). */
export function StatusBadge({ status, label }: { status: string | null | undefined; label?: string }) {
  const text = label ?? humanize(status);
  return <Badge kind={STATUS_KIND[text] ?? "mute"}>{text}</Badge>;
}

/* ---------- layout ---------- */

export function Card({
  title,
  action,
  children,
  className,
  id,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section className={cx("card", className)} id={id}>
      {(title || action) && (
        <div className="card-h">
          {typeof title === "string" ? <h2>{title}</h2> : title}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function PageHead({
  title,
  sub,
  actions,
  crumb,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  crumb?: ReactNode;
}) {
  return (
    <div className="ph">
      <div className="ph-t">
        {crumb && <div className="crumb">{crumb}</div>}
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {actions && <div className="ph-a">{actions}</div>}
    </div>
  );
}

export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {typeof children === "string" ? <p>{children}</p> : children}
      {action}
    </div>
  );
}

export function Kpi({
  label,
  value,
  sub,
  alert,
  onClick,
  to,
  trend,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  alert?: boolean;
  /** Makes the tile a button (a drill-in). */
  onClick?: () => void;
  /** Makes the tile a link to a filtered view (a real link: opens in a new tab too). */
  to?: string;
  /** A small change line under the figure, e.g. "▲ 3 this month (1 last month)". */
  trend?: ReactNode;
}) {
  const body = (
    <>
      <span className="l">{label}</span>
      <span className="v">{value}</span>
      {sub && <span className="s">{sub}</span>}
      {trend && <span className="t">{trend}</span>}
    </>
  );
  if (to)
    return (
      <Link className={cx("kpi", "kpi-btn", "kpi-link", alert && "alert")} to={to}>
        {body}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" className={cx("kpi", "kpi-btn", alert && "alert")} onClick={onClick}>
        {body}
      </button>
    );
  return <div className={cx("kpi", alert && "alert")}>{body}</div>;
}

/** Progress bar; level picks the colour. */
export function Bar({
  pct,
  level = "ok",
  label,
}: {
  pct: number;
  level?: "ok" | "warn" | "high" | "over" | "acc";
  label?: string;
}) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div
      className={`bar ${level}`}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <span style={{ width: `${w}%` }} />
    </div>
  );
}

/** Budget colour: under 75% ok, 75–90 warn, 90–100 high, over 100 over. */
export function budgetLevel(pct: number): "ok" | "warn" | "high" | "over" {
  return pct >= 100 ? "over" : pct >= 90 ? "high" : pct >= 75 ? "warn" : "ok";
}

export function Facts({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="facts">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: "contents" }}>
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === "" ? "—" : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Note({ kind, children }: { kind?: "acc" | "bad"; children: ReactNode }) {
  return <div className={cx("note", kind)}>{children}</div>;
}

/* ---------- forms ---------- */

/** Label + control + hint/error. The control gets the id, and aria-invalid when there's an error. */
export function Field({
  label,
  hint,
  error,
  full,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  full?: boolean;
  children: ReactElement;
}) {
  const id = useId();
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": error || hint ? `${id}-d` : undefined,
      })
    : children;
  const cid = (isValidElement(children) && (children.props as { id?: string }).id) || id;
  return (
    <div className={cx("fld", full && "full")}>
      <label htmlFor={cid}>{label}</label>
      {control}
      {(error || hint) && (
        <span id={`${id}-d`} className="hint" style={error ? { color: "var(--bad)" } : undefined}>
          {error || hint}
        </span>
      )}
    </div>
  );
}

/** The text a node renders (strings and numbers, through elements' children), for a name. */
function plainText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(plainText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return plainText(node.props.children);
  return "";
}

export function Check({
  checked,
  onChange,
  children,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  // Named by its text explicitly too: some readers and accessibility-tree tools follow neither the
  // wrapping label nor aria-labelledby and fall back to the box's value, "on". An aria-label with
  // the same text is read by all of them; aria-labelledby stays for text that isn't plain.
  const id = useId();
  const name = plainText(children).replace(/\s+/g, " ").trim();
  return (
    <label className="check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={name || undefined}
        aria-labelledby={name ? undefined : id}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span id={id}>{children}</span>
    </label>
  );
}

/** Segmented control. */
export function Seg<V extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: V;
  options: [V, ReactNode][];
  onChange: (v: V) => void;
  label?: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([v, l]) => (
        <button
          key={v}
          type="button"
          className={v === value ? "on" : undefined}
          aria-pressed={v === value}
          onClick={() => onChange(v)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/** <option>s from values or [value, label] pairs. */
export function Options({ list }: { list: (string | [string, ReactNode] | [string, ReactNode, string])[] }) {
  return (
    <>
      {list.map((o) => {
        const [v, l, title] = Array.isArray(o) ? (o as [string, ReactNode, string?]) : [o, o];
        return (
          <option key={v} value={v} title={title}>
            {l}
          </option>
        );
      })}
    </>
  );
}

/* ---------- loading & errors ---------- */

/** A page's frame while its first record loads: the breadcrumb, a placeholder title and
 * placeholder tabs and cards, so the layout is there at once (UX-045). */
export function PageSkeleton({
  crumb,
  tabs = 0,
  label = "Loading…",
}: {
  crumb?: ReactNode;
  tabs?: number;
  label?: string;
}) {
  return (
    <div className="wrap" aria-busy="true">
      <div className="ph">
        <div className="ph-t">
          {crumb && <div className="crumb">{crumb}</div>}
          <h1>
            <span className="skel" style={{ width: "12em", height: "1em" }} />
          </h1>
          <div className="sub">
            <span className="skel" style={{ width: "6em" }} />
            <span className="skel" style={{ width: "4em" }} />
          </div>
        </div>
      </div>
      {tabs > 0 && (
        <div className="row" style={{ gap: 16 }}>
          {Array.from({ length: tabs }, (_, i) => (
            <span key={i} className="skel" style={{ width: "5em" }} />
          ))}
        </div>
      )}
      <div className="grid2">
        <div className="card">
          <span className="skel" style={{ width: "60%", height: "6em", display: "block" }} />
        </div>
        <div className="card">
          <span className="skel" style={{ width: "60%", height: "6em", display: "block" }} />
        </div>
      </div>
      <span className="sr-only" role="status">
        {label}
      </span>
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="empty" role="status" aria-live="polite" style={{ borderStyle: "none" }}>
      <p className="muted">{label}</p>
    </div>
  );
}

/** What went wrong, in words a person can act on. */
/** Plain-language messages for the API's error codes, for people who don't read HTTP. */
const FRIENDLY: Record<string, string> = {
  network: "We couldn't reach CoreCRM. Check your internet connection, then try again.",
  forbidden: "You don't have permission to do this. Ask an administrator if you need access.",
  not_found: "We couldn't find this. It may have been deleted, or the link may be out of date.",
  batch_not_found:
    "We couldn't find this. It may already have been restored or emptied from the recycle bin.",
  version_conflict:
    "Someone else saved changes to this while you were editing. Reload the page to see their changes, then make yours again.",
  csrf_failed: "Your session needs refreshing. Reload the page, then try again.",
  unauthenticated: "You've been signed out. Sign in again to carry on.",
  idempotency_in_progress:
    "We're still working on your last request. Wait a moment and check before trying again.",
  email_unavailable: "We couldn't send the email just now. Try again in a few minutes.",
};

/** Codes for requests the app itself got wrong: nothing the person typed can fix them. */
const APP_FAULTS = new Set([
  "if_match_required",
  "invalid_if_match",
  "invalid_cursor",
  "invalid_sort",
  "idempotency_key_reused",
  "idempotency_key_required",
  "invalid_idempotency_key",
]);

/** The API's own wording as a sentence: "a lead needs a title" to "A lead needs a title." */
function sentence(text: string): string {
  const t = text.trim();
  if (!t) return t;
  const s = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** What went wrong, in words anyone can act on. Server faults carry a reference to quote to
    support; ``errorDetails`` has the rest for whoever investigates. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const ref = error.problem.request_id
      ? ` If it keeps happening, quote reference ${error.problem.request_id}.`
      : "";
    if (error.status === 0) return FRIENDLY.network!;
    if (FRIENDLY[error.code]) return FRIENDLY[error.code]!;
    if (error.status === 404) return FRIENDLY.not_found!;
    if (error.status === 429) return "That's too many attempts for now. Wait a minute, then try again.";
    if (error.status >= 500) return `Something went wrong on our side. Try again in a moment.${ref}`;
    if (APP_FAULTS.has(error.code))
      return `Something didn't work as expected. Reload the page and try again.${ref}`;
    const fe = Object.values(error.fieldErrors());
    if (fe.length) return fe.map(sentence).join(" ");
    if (error.code === "validation_failed")
      return "Some of what was entered isn't valid. Check the form and try again.";
    return sentence(error.message);
  }
  return "Something went wrong. Reload the page and try again.";
}

/** The technical side of an error (code, HTTP status, reference, the API's detail), for
    someone fixing it. ``null`` when there's nothing more than the message. */
export function errorDetails(error: unknown): string | null {
  if (error instanceof ApiError) {
    const p = error.problem;
    return [
      `Code: ${p.code}`,
      p.status ? `HTTP ${p.status}` : null,
      p.request_id ? `Reference: ${p.request_id}` : null,
      p.detail ? `Detail: ${p.detail}` : null,
      p.instance ? `Request: ${String(p.instance)}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  }
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return null;
}

/** A collapsed "Technical details" block under an error message. */
export function ErrorDetails({ error }: { error: unknown }) {
  const details = errorDetails(error);
  if (!details) return null;
  return (
    <details className="err-details">
      <summary>Technical details</summary>
      <pre>{details}</pre>
    </details>
  );
}

/** An error inside a form or panel: the friendly message, with the details tucked away. */
export function ErrorNote({ error }: { error: unknown }) {
  return (
    <Note kind="bad">
      {errorMessage(error)}
      <ErrorDetails error={error} />
    </Note>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <EmptyState
      action={
        onRetry && (
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        )
      }
    >
      <p>{errorMessage(error)}</p>
      <ErrorDetails error={error} />
    </EmptyState>
  );
}
